import { Card, CardContent, Stack, Box, Typography, Chip, Divider, IconButton, Tooltip } from "@mui/material";
import { CalendarToday, Person, Schedule, History, CheckCircle, RadioButtonUnchecked } from "@mui/icons-material";

interface CardViewProps {
    questionnaire: any;
    onSelect: (questionnaire: any) => void;
    onViewHistory?: (questionnaire: any) => void;
    hasResponses?: boolean;
    isRespondedInEncounter?: boolean;
}

export function CardView({ questionnaire, onSelect, onViewHistory, hasResponses, isRespondedInEncounter }: CardViewProps) {

    const formatDate = (dateString: string) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("pt-BR");
    };

    const handleHistoryClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (onViewHistory) {
            onViewHistory(questionnaire);
        }
    };

    return (
        <Card
            sx={{
                boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                cursor: "pointer",
                border: "1px solid",
                borderColor: "divider",
                transition: "all 0.2s ease-in-out",
                position: "relative",
                "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: 1,
                },
                borderRadius: "16px",
            }}
            onClick={() => onSelect(questionnaire)}
        >
            <CardContent>
                <Stack spacing={1.5}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Typography
                            variant="body1"
                            fontWeight="500"
                            color="text.primary"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                            }}
                        >
                            {questionnaire.title || "Sem título"}
                        </Typography>
                        {isRespondedInEncounter ? (
                            <Tooltip title="Respondido neste atendimento" placement="top">
                                <CheckCircle
                                    sx={{
                                        color: "success.main",
                                        fontSize: 20,
                                        ml: 1
                                    }}
                                />
                            </Tooltip>
                        ) : (
                            <Tooltip title="Não respondido neste atendimento" placement="top">
                                <RadioButtonUnchecked
                                    sx={{
                                        color: "warning.main",
                                        fontSize: 20,
                                        ml: 1
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Box>

                    <Divider />

                    {questionnaire.description && (
                        <Box>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: "vertical",
                                    lineHeight: 1.4,
                                }}
                            >
                                {questionnaire.description}
                            </Typography>
                        </Box>
                    )}

                    <Stack spacing={1}>
                        {questionnaire.publisher && (
                            <Box display="flex" alignItems="center" gap={1}>
                                <Person fontSize="small" color="action" />
                                <Typography variant="caption" color="text.secondary">
                                    {questionnaire.publisher}
                                </Typography>
                            </Box>
                        )}

                        {questionnaire.date && (
                            <Box display="flex" alignItems="center" gap={1}>
                                <CalendarToday fontSize="small" color="action" />
                                <Typography variant="caption" color="text.secondary">
                                    Criado em: {formatDate(questionnaire.date)}
                                </Typography>
                            </Box>
                        )}

                        {questionnaire.lastReviewDate && (
                            <Box display="flex" alignItems="center" gap={1}>
                                <Schedule fontSize="small" color="action" />
                                <Typography variant="caption" color="text.secondary">
                                    Última revisão: {formatDate(questionnaire.lastReviewDate)}
                                </Typography>
                            </Box>
                        )}
                    </Stack>

                    {questionnaire.version && (
                        <Box>
                            <Chip
                                label={`Versão ${questionnaire.version}`}
                                size="small"
                                variant="outlined"
                                color="primary"
                            />
                        </Box>
                    )}
                </Stack>
            </CardContent>

            {onViewHistory && hasResponses && (
                <Tooltip title="Ver histórico do questionário" placement="top">
                    <IconButton
                        onClick={handleHistoryClick}
                        sx={{
                            position: "absolute",
                            bottom: 12,
                            right: 12,
                            backgroundColor: "neutral200.main",
                            color: "neutral600.main",
                            width: 36,
                            height: 36,
                            border: "1px solid",
                            borderColor: "neutral300.main",
                            boxShadow: 0,
                            "&:hover": {
                                backgroundColor: "neutral300.main",
                                color: "neutral600.main",
                                transform: "scale(1.05)",
                                boxShadow: 2,
                            },
                            transition: "all 0.2s ease-in-out",
                            zIndex: 1,
                        }}
                    >
                        <History fontSize="medium" />
                    </IconButton>
                </Tooltip>
            )}
        </Card>
    );
}